import { FC, PropsWithChildren, useEffect } from 'react';
import semver from 'semver';
import { useAsync, useAsyncFn } from 'react-use';

import { AppUpdate } from '@capawesome/capacitor-app-update';
import { Capacitor } from '@capacitor/core';
import {
  Button,
  Content,
  Header,
  Page,
  Typography,
  useUtils,
} from '@wearehelpful/ui-kit';
import { useTranslation } from '@wearehelpful/ui-localization';
import { useSplash } from '@native/splash';

import { Container, Image } from './Upgrade.style';
import IMG_SRC from './Upgrade.png';

export type UpgradeProps = PropsWithChildren<{}>;

export const Upgrade: FC<UpgradeProps> = ({ children }) => {
  const { t } = useTranslation();

  const { hide } = useSplash();
  const { toast } = useUtils();

  const { loading, value } = useAsync(async () => {
    if (Capacitor.isNativePlatform()) {
      const {
        availableVersionName,
        currentVersionName,
        currentVersionCode = '0',
        availableVersionCode = '0',
      } = await AppUpdate.getAppUpdateInfo();

      const available = `${availableVersionName}.${availableVersionCode}`;
      const current = `${currentVersionName}.${currentVersionCode}`;

      return (
        semver.valid(available) &&
        semver.valid(current) &&
        semver.gt(available, current)
      );
    }

    return false;
  });

  const [{ loading: opening }, openAppStore] = useAsyncFn(() => {
    return AppUpdate.openAppStore();
  });

  const handleOnGoToAppStore = async () => {
    try {
      await openAppStore();
    } catch (error) {
      if (error instanceof Error) {
        toast({
          variant: 'error',
          message: error.message,
        });
      } else {
        toast({
          variant: 'error',
          message: t('errorCodes.default'),
        });
      }
    }
  };

  useEffect(() => {
    if (value) hide();
  }, [value, hide]);

  if (loading) return null;

  if (value) {
    return (
      <Page>
        <Header />
        <Content>
          <Container>
            <Typography variant="h2" align="center">
              {t('subheadings.upgrade.title')}
            </Typography>
            <Typography variant="p1" align="center">
              {t('subheadings.upgrade.description')}
            </Typography>
            <Image src={IMG_SRC} />
            <Button
              variant="secondary"
              loading={opening}
              onClick={handleOnGoToAppStore}
            >
              {t('actions.goToAppStore')}
            </Button>
          </Container>
        </Content>
      </Page>
    );
  }

  return <>{children}</>;
};
